package devxertech.com.pages.commons

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.disabled
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onFocusIn
import com.varabyte.kobweb.compose.ui.modifiers.onFocusOut
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import devxertech.com.style.LoginInputStyleDefault
import devxertech.com.style.LoginInputStyleFocus
import devxertech.com.theme.LocalColorMode
import devxertech.com.theme.toSitePalette
import devxertech.com.utils.Constants.FONT_FAMILY
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement

@Composable
fun OptionalInputIconField(
    value: String = "",
    onValueChange: (String) -> Unit = {},
    inputType: InputType<String> = InputType.Text,
    placeHolder: String,
    width: CSSLengthOrPercentageNumericValue = 100.px,
    height: CSSLengthNumericValue = 39.px,
    id: String = "",
    marginBottom: CSSLengthNumericValue = 0.px,
    marginLeft: CSSLengthNumericValue = 0.px,
    marginRight: CSSLengthNumericValue = 0.px,
    fontSize: CSSLengthNumericValue = 12.px,
    disabled: Boolean = false,
    placeHolderAlignRight: Boolean = false,
    onEnterPress: ((String) -> String)? = null,
    number: Boolean = false,
    onFocusChanged: ((Boolean) -> Unit)? = null,
    leadingIcon: (@Composable (() -> Unit))? = null,  // ✅ New
    trailingIcon: (@Composable (() -> Unit))? = null  // ✅ New
) {
    var isDateFocused by remember { mutableStateOf(false) }
    var modifier by remember { mutableStateOf(LoginInputStyleDefault) }

    val effectiveType =
        if (inputType == InputType.Date && !isDateFocused) InputType.Text else inputType
    val colorMode = LocalColorMode.current
    val sitePalette = colorMode.toSitePalette()

    modifier =
        if (disabled) modifier.border(width = 0.px) else (if (modifier == LoginInputStyleFocus) LoginInputStyleFocus else LoginInputStyleDefault)

    Row(
        modifier = Modifier
            .width(width)
            .height(height)
            .margin(bottom = marginBottom, right = marginRight, left = marginLeft)
            .backgroundColor(if (disabled) sitePalette.textFieldBorderColorDisabled.rgb else sitePalette.modalBackgroundColor.rgb)
            .borderRadius(5.px)
            .border(
                width = 1.px,
                style = LineStyle.Solid,
                color = if (disabled) sitePalette.textFieldBorderColorDisabled.rgb else sitePalette.commonSecondaryButtonColor.rgb
            )
            .alignItems(AlignItems.Center)
            .padding(leftRight = 10.px)
    ) {
        leadingIcon?.invoke()

        Input(
            type = effectiveType,
            attrs = modifier
                .id(id)
                .disabled(disabled)
                .width(93.percent)
                .color(if (disabled) sitePalette.textFieldSupportiveTextColor.rgb else sitePalette.textFieldTextColor.rgb)
                .backgroundColor(Colors.Transparent) // Transparent to avoid duplicate bg
                .border(width = 0.px)
                .outline(width = 0.px, style = LineStyle.None, color = Colors.Transparent)
                .padding(8.px)
                .fontSize(fontSize)
                .textAlign(if (placeHolderAlignRight) TextAlign.End else TextAlign.Start)
                .onFocusIn {
                    if (inputType == InputType.Date) isDateFocused = true
                    modifier = LoginInputStyleFocus
                    onFocusChanged?.invoke(true)
                }
                .onFocusOut {
                    if (inputType == InputType.Date && value.isEmpty()) isDateFocused = false
                    modifier = LoginInputStyleDefault
                    onFocusChanged?.invoke(false)
                }
                .onKeyDown {
                    if (number) {
                        val allowedKeys = listOf(
                            "Backspace", "ArrowLeft", "ArrowRight")
                        val operatorKeys = listOf("+", "-", "*", "/")
                        val regex = Regex("[0-9]") // only digits; operators handled separately
                        val inputField = document.getElementById(id) as HTMLInputElement
                        val currentValue = inputField.value

                        val key = it.key

                        // ✅ Allow system keys
                        if (key in allowedKeys) return@onKeyDown

                        // ✅ Allow digits
                        if (regex.matches(key)) return@onKeyDown

                        // ✅ Allow operators, but prevent two in a row or starting with one
                        if (key in operatorKeys) {
                            val lastChar = currentValue.lastOrNull()?.toString()
                            if (currentValue.isEmpty() || lastChar in operatorKeys) {
                                it.preventDefault()
                            }
                            return@onKeyDown
                        }

                        // ❌ Block everything else
                        it.preventDefault()
                    }
                }.styleModifier {
                    property("font-family", FONT_FAMILY)
                }
                .toAttrs {
                    if (inputType != InputType.Date || !isDateFocused) {
                        attr("placeholder", placeHolder)
                    }
                    attr("value", value)
                    onInput { event -> onValueChange(event.value) }

                    onKeyDown { event ->
                        if (event.key == "Enter") {
                            event.preventDefault()
                            if (onEnterPress != null) {
                                val value1 =
                                    (document.getElementById(id) as? HTMLInputElement)?.value ?: ""
                                val newValue = onEnterPress(value1)
                                onValueChange(newValue)
                            }
                            (document.getElementById(id) as? HTMLInputElement)?.blur()
                        }
                    }
                }
        )

        // ✅ Trailing icon (e.g., clear text button)
        trailingIcon?.invoke()
    }
}

